/**
 * Routes Transfer Validation Framework
 * 
 * Unified validation framework for upload, download, and delete operations.
 * Provides consistent validation interfaces and error handling across all operations.
 * 
 * @module routesTransferValidation
 */

import type { FixedRoute, FixedRouteStop } from '../types/fixedRoutes';
import {
  validateRoutesForUpload,
  type ValidationResult as UploadValidationResult,
  type DuplicateRoute,
} from './fixedRouteUatValidator';
import { exportRouteData, validateRouteExport } from './fixedRouteDataExporter';
import type { UATRoute } from './fixedRouteUatClient';
import type { ValidationSummaryData, RouteValidationIssue } from '../components/map/components/routes-transfer/ValidationSummary';

/**
 * Prerequisites for route operations
 */
export interface OperationPrerequisites {
  /** JWT token for authentication */
  jwtToken: string;
  /** Selected product ID */
  productId: string;
  /** Selected network ID */
  networkId: number | null;
  /** Service date (YYYY-MM-DD) */
  serviceDate?: string;
  /** Timezone string */
  timezone?: string;
  /** Whether download acknowledgment is checked */
  downloadAcknowledged?: boolean;
  /** Selected route IDs */
  selectedRouteIds: Set<string>;
}

/**
 * Validate prerequisites for any operation
 * 
 * @param prerequisites - Prerequisites to validate
 * @returns Validation result
 */
export function validatePrerequisites(
  prerequisites: OperationPrerequisites
): { isValid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  // JWT token validation
  if (!prerequisites.jwtToken || !prerequisites.jwtToken.trim()) {
    errors.push('JWT token is required');
  }

  // Product ID validation
  if (!prerequisites.productId) {
    errors.push('Product/Network must be selected');
  }

  // Network ID validation (if required)
  if (prerequisites.networkId === null || prerequisites.networkId === undefined) {
    warnings.push('Network ID is not set. Some operations may fail.');
  }

  // Service date validation (optional but recommended)
  if (!prerequisites.serviceDate) {
    warnings.push('Service date is not set. Some operations may be limited.');
  }

  // Timezone validation (optional but recommended)
  if (!prerequisites.timezone) {
    warnings.push('Timezone is not set. Some operations may be limited.');
  }

  // Download acknowledgment validation (if required)
  if (prerequisites.downloadAcknowledged === false) {
    errors.push('You must acknowledge the download terms before proceeding');
  }

  // Selected routes validation
  if (prerequisites.selectedRouteIds.size === 0) {
    errors.push('At least one route must be selected');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Upload validation result
 */
export interface UploadValidationData extends ValidationSummaryData {
  /** Duplicate routes found */
  duplicates?: DuplicateRoute[];
  /** Suggested version names for routes */
  suggestedVersionNames?: Map<string, string>;
  /** Coordinate matching results (if applicable) */
  coordinateMatchingResults?: {
    matchedStops: number;
    unmatchedStops: number;
    ambiguousMatches: number;
  };
}

/**
 * Validate routes for upload operation
 * 
 * @param routes - Routes to validate
 * @param stops - All available stops
 * @param prerequisites - Operation prerequisites
 * @returns Validation result
 */
export async function validateForUpload(
  routes: FixedRoute[],
  stops: FixedRouteStop[],
  prerequisites: OperationPrerequisites
): Promise<UploadValidationData> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const routeIssues: RouteValidationIssue[] = [];

  // Step 1: Validate prerequisites
  const prerequisiteValidation = validatePrerequisites(prerequisites);
  errors.push(...prerequisiteValidation.errors);
  warnings.push(...prerequisiteValidation.warnings);

  // Step 2: Validate each route
  for (const route of routes) {
    const routeErrors: string[] = [];
    const routeWarnings: string[] = [];

    // Check route exists
    if (!route) {
      routeErrors.push('Route is missing or invalid');
      routeIssues.push({
        routeId: 'unknown',
        routeName: 'Unknown',
        errors: routeErrors,
        warnings: routeWarnings,
      });
      continue;
    }

    // Check route has at least 2 stops
    if (!route.stops || route.stops.length < 2) {
      routeErrors.push('Route must have at least 2 stops');
    }

    // Check route has valid start time
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    if (!route.startTime || !timeRegex.test(route.startTime)) {
      routeErrors.push('Route must have a valid start time (HH:mm format)');
    }

    // Check all stops have valid coordinates
    if (route.stops) {
      for (const stopConfig of route.stops) {
        const stop = stops.find((s) => s.id === stopConfig.stopId);
        if (!stop) {
          routeErrors.push(`Stop "${stopConfig.stopId}" not found`);
        } else {
          if (!stop.coordinates || typeof stop.coordinates.lat !== 'number' || typeof stop.coordinates.lng !== 'number') {
            routeErrors.push(`Stop "${stop.name}" is missing coordinates`);
          } else {
            // Validate coordinate ranges
            if (stop.coordinates.lat < -90 || stop.coordinates.lat > 90) {
              routeErrors.push(`Stop "${stop.name}" has invalid latitude`);
            }
            if (stop.coordinates.lng < -180 || stop.coordinates.lng > 180) {
              routeErrors.push(`Stop "${stop.name}" has invalid longitude`);
            }
          }
        }
      }
    }

    // Use existing validation function for detailed validation
    try {
      const exportedData = exportRouteData(route, stops);
      const validation = validateRouteExport(exportedData);

      if (!validation.isValid) {
        routeErrors.push(...validation.errors);
      }

      if (validation.warnings.length > 0) {
        routeWarnings.push(...validation.warnings);
      }
    } catch (error) {
      routeErrors.push(`Failed to validate route: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    // Add route issues if any
    if (routeErrors.length > 0 || routeWarnings.length > 0) {
      routeIssues.push({
        routeId: route.id,
        routeName: route.name,
        errors: routeErrors,
        warnings: routeWarnings,
      });
    }
  }

  // Step 3: Use existing comprehensive validation (if prerequisites are met)
  let uploadValidation: UploadValidationResult | null = null;
  if (
    prerequisites.jwtToken &&
    prerequisites.productId &&
    prerequisites.networkId !== null &&
    prerequisites.serviceDate &&
    prerequisites.timezone
  ) {
    try {
      uploadValidation = await validateRoutesForUpload(
        routes,
        stops,
        prerequisites.networkId,
        prerequisites.jwtToken,
        prerequisites.productId,
        '',
        prerequisites.serviceDate,
        prerequisites.timezone
      );

      // Merge validation results
      errors.push(...uploadValidation.errors);
      warnings.push(...uploadValidation.warnings);
    } catch (error) {
      warnings.push(`Could not perform comprehensive validation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Build coordinate matching results (if applicable)
  let coordinateMatchingResults: UploadValidationData['coordinateMatchingResults'] | undefined;
  if (uploadValidation) {
    // Count stops with and without UAT stop IDs
    let matchedStops = 0;
    let unmatchedStops = 0;

    for (const route of routes) {
      for (const stopConfig of route.stops) {
        const stop = stops.find((s) => s.id === stopConfig.stopId);
        if (stop) {
          if (stop.uatStopId) {
            matchedStops++;
          } else {
            unmatchedStops++;
          }
        }
      }
    }

    coordinateMatchingResults = {
      matchedStops,
      unmatchedStops,
      ambiguousMatches: 0, // TODO: Implement ambiguous match detection
    };
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    routeIssues: routeIssues.length > 0 ? routeIssues : undefined,
    duplicates: uploadValidation?.duplicates,
    suggestedVersionNames: uploadValidation?.suggestedVersionNames,
    coordinateMatchingResults,
  };
}

/**
 * Download validation result
 */
export interface DownloadValidationData extends ValidationSummaryData {
  /** Routes that can be downloaded */
  downloadableRoutes?: UATRoute[];
  /** Routes that cannot be downloaded (with reasons) */
  nonDownloadableRoutes?: Array<{
    route: UATRoute;
    reason: string;
  }>;
  /** Duplicate detection results */
  duplicates?: Array<{
    route: UATRoute;
    existingRouteId: string;
    existingRouteName: string;
  }>;
}

/**
 * Validate routes for download operation
 * 
 * @param routes - Routes to validate (from database)
 * @param prerequisites - Operation prerequisites
 * @returns Validation result
 */
export async function validateForDownload(
  routes: UATRoute[],
  prerequisites: OperationPrerequisites
): Promise<DownloadValidationData> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const routeIssues: RouteValidationIssue[] = [];
  const downloadableRoutes: UATRoute[] = [];
  const nonDownloadableRoutes: Array<{ route: UATRoute; reason: string }> = [];

  // Step 1: Validate prerequisites
  const prerequisiteValidation = validatePrerequisites(prerequisites);
  errors.push(...prerequisiteValidation.errors);
  warnings.push(...prerequisiteValidation.warnings);

  // Step 2: Validate each route
  for (const route of routes) {
    const routeErrors: string[] = [];
    const routeWarnings: string[] = [];

    // Check route exists
    if (!route || !route.id) {
      routeErrors.push('Route is missing or invalid');
      nonDownloadableRoutes.push({
        route: route as UATRoute,
        reason: 'Route is missing or invalid',
      });
      continue;
    }

    // Check route has stops
    if (!route.stops || route.stops.length < 2) {
      routeErrors.push('Route must have at least 2 stops');
      nonDownloadableRoutes.push({
        route,
        reason: 'Route must have at least 2 stops',
      });
      continue;
    }

    // Route is downloadable
    downloadableRoutes.push(route);

    // Add route issues if any
    if (routeErrors.length > 0 || routeWarnings.length > 0) {
      routeIssues.push({
        routeId: route.id,
        routeName: route.label || route.id,
        errors: routeErrors,
        warnings: routeWarnings,
      });
    }
  }

  // Step 3: Check for duplicates in Atlas (if we have access to Atlas routes)
  // This would require access to FixedRoutesContext, which is not available here
  // Duplicate detection will be handled at the component level

  return {
    isValid: errors.length === 0 && nonDownloadableRoutes.length === 0,
    errors,
    warnings,
    routeIssues: routeIssues.length > 0 ? routeIssues : undefined,
    downloadableRoutes: downloadableRoutes.length > 0 ? downloadableRoutes : undefined,
    nonDownloadableRoutes: nonDownloadableRoutes.length > 0 ? nonDownloadableRoutes : undefined,
  };
}

/**
 * Delete validation result
 */
export interface DeleteValidationData extends ValidationSummaryData {
  /** Routes that can be deleted */
  deletableRoutes?: UATRoute[];
  /** Routes that cannot be deleted (with reasons) */
  nonDeletableRoutes?: Array<{
    route: UATRoute;
    reason: string;
  }>;
}

/**
 * Validate routes for delete operation
 * 
 * @param routes - Routes to validate (from database)
 * @param prerequisites - Operation prerequisites
 * @returns Validation result
 */
export async function validateForDelete(
  routes: UATRoute[],
  prerequisites: OperationPrerequisites
): Promise<DeleteValidationData> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const routeIssues: RouteValidationIssue[] = [];
  const deletableRoutes: UATRoute[] = [];
  const nonDeletableRoutes: Array<{ route: UATRoute; reason: string }> = [];

  // Step 1: Validate prerequisites
  const prerequisiteValidation = validatePrerequisites(prerequisites);
  errors.push(...prerequisiteValidation.errors);
  warnings.push(...prerequisiteValidation.warnings);

  // Step 2: Validate each route
  for (const route of routes) {
    const routeErrors: string[] = [];
    const routeWarnings: string[] = [];

    // Check route exists
    if (!route || !route.id) {
      routeErrors.push('Route is missing or invalid');
      nonDeletableRoutes.push({
        route: route as UATRoute,
        reason: 'Route is missing or invalid',
      });
      continue;
    }

    // Note: Permission and lock checks would typically be done via API
    // For now, we assume all routes are deletable if they exist
    // The API will return appropriate errors if deletion is not allowed

    deletableRoutes.push(route);

    // Add route issues if any
    if (routeErrors.length > 0 || routeWarnings.length > 0) {
      routeIssues.push({
        routeId: route.id,
        routeName: route.label || route.id,
        errors: routeErrors,
        warnings: routeWarnings,
      });
    }
  }

  // Add warning if no routes can be deleted
  if (deletableRoutes.length === 0 && routes.length > 0) {
    warnings.push('No routes can be deleted. Please check permissions and route status.');
  }

  return {
    isValid: errors.length === 0 && nonDeletableRoutes.length === 0,
    errors,
    warnings,
    routeIssues: routeIssues.length > 0 ? routeIssues : undefined,
    deletableRoutes: deletableRoutes.length > 0 ? deletableRoutes : undefined,
    nonDeletableRoutes: nonDeletableRoutes.length > 0 ? nonDeletableRoutes : undefined,
  };
}

